import { Link } from "react-router-dom";
import { useCMSPage } from "../../../context/CMSContext";
import { useCMSGlobal } from "../../../hooks/useCMSField";

const FALLBACK_COLUMNS = [
  {
    title: "Services",
    links: [
      { label: "Rubbish Removal",          href: "/rubbish-removal"             },
      { label: "Man & Van Clearance",       href: "/man-and-van-waste-clearance" },
      { label: "Sofa Removal",              href: "/sofa-removal"                },
      { label: "Mattress Removal",          href: "/mattress-removal"            },
      { label: "House Clearance",           href: "/house-clearance"             },
      { label: "Garden Waste Removal",      href: "/garden-waste-removal"        },
      { label: "Builders Waste Removal",    href: "/builders-waste-removal"      },
      { label: "Office Clearance",          href: "/office-clearance"            },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us",  href: "/about"    },
      { label: "News",      href: "/news"     },
      { label: "Locations", href: "/locations"},
      { label: "Contact",   href: "/contact"  },
      { label: "Account",   href: "/account"  },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms",   href: "/terms"   },
      { label: "Privacy", href: "/privacy" },
      { label: "Cookies", href: "/cookies" },
    ],
  },
];

export default function Footer() {
  const globalCMS = useCMSGlobal();
  const { data: footerCMS } = useCMSPage("footer");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const main: any = (footerCMS as any)?.main ?? {};

  const tagline       = main?.tagline       ?? "Rubbish removal. Done right.";
  const wasteLicence  = main?.waste_licence ?? globalCMS?.waste_licence ?? "CBDU61392";
  const copyrightYear = main?.copyright_year ?? "2026";
  const columns       = main?.columns?.length ? main.columns : FALLBACK_COLUMNS;
  const phone         = globalCMS?.phone ?? "03335 775445";
  const email         = globalCMS?.email ?? "hello@cleario.co.uk";
  const social        = globalCMS?.social ?? {};

  return (
    <footer className="bg-cl-dark text-white pt-16 pb-8">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand column */}
          <div>
            <Link to="/" className="flex items-center gap-1 mb-4 cursor-pointer">
              <span className="text-2xl font-black tracking-tight">
                Clear<span className="text-cl-green">io</span>
              </span>
              <span className="w-2.5 h-2.5 rounded-full bg-cl-green mb-4 inline-block border-2 border-white"></span>
            </Link>
            <p className="text-white/60 text-sm font-semibold leading-relaxed mb-5">{tagline}</p>
            <div className="flex flex-col gap-2 text-sm">
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors font-semibold">
                <i className="ri-phone-fill text-cl-green flex-shrink-0"></i>
                {phone}
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors font-semibold">
                <i className="ri-mail-fill text-cl-green flex-shrink-0"></i>
                {email}
              </a>
            </div>
            {/* Social icons */}
            <div className="flex gap-3 mt-5">
              {Object.entries(social).map(([key, url]) => {
                const iconMap: Record<string, string> = {
                  facebook: "ri-facebook-fill",
                  instagram: "ri-instagram-line",
                  twitter: "ri-twitter-x-fill",
                  linkedin: "ri-linkedin-fill",
                };
                return (
                  <a
                    key={key}
                    href={url as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-cl-green transition-colors"
                  >
                    <i className={`${iconMap[key] ?? "ri-link"} text-sm`}></i>
                  </a>
                );
              })}
            </div>
          </div>

          {/* Dynamic columns from CMS */}
          {columns.map((col: { title: string; links: { label: string; href: string }[] }) => (
            <div key={col.title}>
              <h4 className="font-black text-sm uppercase tracking-widest text-white/40 mb-4">{col.title}</h4>
              <ul className="flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-white/70 hover:text-white font-semibold transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t-2 border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40 font-semibold">
            Waste Carrier Licence: {wasteLicence} &nbsp;·&nbsp; &copy; Cleario {copyrightYear}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-[#00b67a] text-xs"></i>
              ))}
            </div>
            <span className="text-white/40 text-xs font-semibold">Excellent on Trustpilot</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
